import React from "react";
import { usePGlite } from "@electric-sql/pglite-react";
import { TableCellsIcon, ClockIcon, TrashIcon } from "@heroicons/react/24/outline";
import { toast } from "sonner";

import { useSqlDashboardStore } from "@/lib/store";

export function Sidebar() {
  const [activeTab, setActiveTab] = React.useState<"tables" | "history">(
    "tables"
  );

  const db = usePGlite();
  const [tables, setTables] = React.useState<string[]>([]);
  const { sidebarRefreshTrigger } = useSqlDashboardStore();

  React.useEffect(() => {
    if (!db) return;
    db.query<{ table_name: string }>(
      `SELECT table_name FROM information_schema.tables WHERE table_schema='public' ORDER BY table_name`
    ).then((res) => {
      setTables(res.rows.map((r) => r.table_name));
    });
  }, [db, sidebarRefreshTrigger]);

  const { setQuery, queryHistory, addToHistory, clearHistory } = useSqlDashboardStore();

  const handleTableClick = (tableName: string) => {
    const q = `SELECT * FROM ${tableName} LIMIT 50;`;
    setQuery(q);
    addToHistory(q);
    toast.info(`Loaded query for ${tableName}`);
  };

  const handleHistoryClick = (q: string) => {
    setQuery(q);
    toast.info("Query loaded from history");
  };

  const handleClearHistory = () => {
    clearHistory();
    toast.success("History cleared");
  };

  return (
    <div className="h-full flex flex-col bg-sidebar border-r border-sidebar-border">
      <div className="px-4 py-3 border-b border-sidebar-border">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/60">
          Explorer
        </h2>
      </div>

      <div className="flex border-b border-sidebar-border">
        <button
          onClick={() => setActiveTab("tables")}
          className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-2 transition-colors border-b-2 ${
            activeTab === "tables"
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <TableCellsIcon className="w-3.5 h-3.5" />
          Tables
        </button>
        <button
          onClick={() => setActiveTab("history")}
          className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-2 transition-colors border-b-2 ${
            activeTab === "history"
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <ClockIcon className="w-3.5 h-3.5" />
          History
          {queryHistory.length > 0 && (
            <span className="text-[10px] bg-primary/10 text-primary px-1.5 rounded-full">
              {queryHistory.length}
            </span>
          )}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-1.5">
        {activeTab === "tables" ? (
          <div className="space-y-px">
            {tables.length === 0 ? (
              <div className="text-xs text-muted-foreground p-6 text-center">
                <p className="mb-1">No tables found</p>
                <p className="text-muted-foreground/60">
                  Use Seed DB to load sample data
                </p>
              </div>
            ) : (
              tables.map((table) => (
                <button
                  key={table}
                  onClick={() => handleTableClick(table)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left rounded-md hover:bg-sidebar-accent text-sidebar-foreground/80 hover:text-sidebar-accent-foreground transition-colors group"
                >
                  <TableCellsIcon className="w-3.5 h-3.5 text-muted-foreground group-hover:text-sidebar-primary shrink-0 transition-colors" />
                  <span className="truncate">{table}</span>
                </button>
              ))
            )}
          </div>
        ) : (
          <div className="space-y-px">
            {queryHistory.length === 0 ? (
              <div className="flex flex-col items-center gap-2 text-muted-foreground p-6 text-center">
                <ClockIcon className="w-6 h-6 opacity-30" />
                <p className="text-xs">No history yet</p>
                <p className="text-[11px] opacity-60">Run a query to see it here</p>
              </div>
            ) : (
              <>
                <div className="flex justify-end px-1 mb-1">
                  <button
                    onClick={handleClearHistory}
                    className="text-[10px] text-muted-foreground hover:text-destructive flex items-center gap-1 px-2 py-1 rounded transition-colors"
                  >
                    <TrashIcon className="w-3 h-3" />
                    Clear
                  </button>
                </div>
                {queryHistory.map((q, i) => (
                  <button
                    key={`${i}-${q.slice(0, 20)}`}
                    onClick={() => handleHistoryClick(q)}
                    className="w-full text-left px-3 py-2 text-xs font-mono rounded-md hover:bg-sidebar-accent text-sidebar-foreground/70 hover:text-sidebar-accent-foreground transition-colors truncate"
                    title={q}
                  >
                    <span className="line-clamp-2 break-all">{q}</span>
                  </button>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
